import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, Smartphone, Brain, TrendingUp, ArrowRight, Sparkle, ArrowDown, Phone, Settings, CreditCard } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const HowItWorks = () => {
  const [activeProduct, setActiveProduct] = useState<'virtual-host' | 'virtual-assistant'>('virtual-host');

  // Virtual Host steps (AI Phone Ordering)
  const virtualHostSteps = [
    {
      icon: Store,
      number: "01",
      title: "Restaurant Signs Up",
      description: "Choose a subscription plan and upload your PDF menu",
      details: ["Simple pricing plans", "Instant PDF processing", "5-minute setup"]
    },
    {
      icon: Phone,
      number: "02",
      title: "Pick Your Phone Number",
      description: "Choose a local or toll-free number that customers will call for orders",
      details: ["Local or toll-free options", "Instant activation", "Forwards calls to existing number"]
    },
    {
      icon: Settings,
      number: "03",
      title: "Configure AI Host",
      description: "Set your greeting, business hours, and let AI learn your menu",
      details: ["Custom voice greeting", "Set business hours", "AI learns your specials"]
    },
    {
      icon: CreditCard,
      number: "04",
      title: "Start Taking Orders",
      description: "AI answers calls 24/7, takes orders, and processes payments automatically",
      details: ["Stripe Connected Account setup", "Next day payments", "Orders sent to POS"]
    }
  ];

  // Virtual Assistant steps (Digital Menu)
  const virtualAssistantSteps = [
    {
      icon: Store,
      number: "01",
      title: "Restaurant Signs Up",
      description: "Choose a subscription plan and upload your PDF menu",
      details: ["Multiple subscription tiers", "Instant PDF processing", "Quick setup process"]
    },
    {
      icon: Smartphone,
      number: "02",
      title: "Customer Downloads App",
      description: "Customers find your restaurant and browse your AI-enhanced menu",
      details: ["Location-based search", "Real-time availability", "Easy restaurant discovery"]
    },
    {
      icon: Brain,
      number: "03",
      title: "Virtual Staff Always On",
      description: "AI handles customer questions 24/7, never calls in sick or misses a shift",
      details: ["Answers menu questions", "Handles dietary requests", "Works every shift"]
    },
    {
      icon: TrendingUp,
      number: "04",
      title: "Watch Revenue Grow",
      description: "Track performance and see your virtual staff's impact on revenue",
      details: ["Revenue analytics", "Customer insights", "Data-driven decisions"]
    }
  ];

  const steps = activeProduct === 'virtual-host' ? virtualHostSteps : virtualAssistantSteps;

  return (
    <section id="how-it-works" className="py-12 bg-[#2256e5] relative overflow-hidden">
      {/* Background elements for visual interest */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-dineai-yellow/5 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 rounded-full bg-white/5 blur-2xl"></div>
      </div>

      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
              <Sparkle className="w-4 h-4" />
              How It Works
            </span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
            Simple Yet <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">Revolutionary</span>
          </h2>
          
          <p className="text-white/80 text-base max-w-2xl mx-auto mb-6">
            From restaurant subscription to customer delight in four seamless steps
          </p>

          {/* Product Toggle Tabs */}
          <div className="flex justify-center">
            <div className="inline-flex bg-white/10 rounded-full p-1 backdrop-blur-sm border border-white/20">
              <button
                onClick={() => setActiveProduct('virtual-host')}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeProduct === 'virtual-host'
                    ? 'bg-dineai-yellow text-black shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Phone className="w-4 h-4" />
                AI Virtual Host
              </button>
              <button
                onClick={() => setActiveProduct('virtual-assistant')}
                className={`px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeProduct === 'virtual-assistant'
                    ? 'bg-dineai-yellow text-black shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                AI Virtual Assistant
              </button>
            </div>
          </div>
        </motion.div>

        {/* Steps */}
        <div className="max-w-7xl mx-auto mt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeProduct}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
            {steps.map((step, index) => (
              <motion.div
                key={`${activeProduct}-${index}`}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {/* Connecting line for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-6 -right-4 w-8 z-0">
                    <ArrowRight className="w-4 h-4 text-dineai-yellow/40" />
                  </div>
                )}
                
                <div className="relative rounded-xl p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 border border-yellow-400/30 group-hover:-translate-y-1 h-full flex flex-col backdrop-blur-sm" style={{ backgroundColor: '#3256dc' }}>
                  {/* Step number */}
                  <div className="absolute -top-2 -left-2 w-8 h-8 bg-dineai-yellow rounded-lg flex items-center justify-center shadow-lg">
                    <span className="text-dineai-blue text-base font-bold">{step.number}</span>
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <step.icon className="w-5 h-5 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-base font-bold text-white mb-3 min-h-[2.5rem] flex items-start">
                    {step.title}
                  </h3>
                  
                  <p className="text-white/80 text-sm mb-4 flex-grow min-h-[3rem]">
                    {step.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <div key={detailIndex} className="flex items-center gap-2 text-sm text-white/60">
                        <div className="w-1 h-1 rounded-full bg-dineai-yellow"></div>
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Strong CTA Section - No Box */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 font-['Poppins',_sans-serif]">
            From Setup to Sales – <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">AI That Works</span>
          </h3>
          <p className="text-white/90 text-base sm:text-lg mb-8 max-w-2xl mx-auto px-4 sm:px-0">
            Transform your restaurant with 24/7 AI staff that never misses an opportunity to serve customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/restaurant-menu-search">
              <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                BOOST YOUR SALES
              </Button>
            </Link>
            <Link to={activeProduct === 'virtual-host' ? '/products/virtual-host#pricing' : '/pricing'}>
              <Button className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all flex items-center gap-2">
                SEE PRICING PLANS
                <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
              </Button>
            </Link>
          </div>
          
          {/* Bridge to next section */}
          <motion.div 
            className="mt-16 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div 
              className="relative group cursor-pointer"
              onClick={() => {
                const element = document.getElementById('revenue-impact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-dineai-yellow/20 via-dineai-yellow/30 to-dineai-yellow/20 blur-xl group-hover:blur-2xl transition-all duration-300"></div>
              <div className="relative bg-gradient-to-r from-dineai-yellow/10 via-dineai-yellow/20 to-dineai-yellow/10 backdrop-blur-sm border border-dineai-yellow/30 rounded-2xl px-8 py-4 group-hover:border-dineai-yellow/50 transition-all duration-300">
                <p className="text-white/90 text-base font-medium flex items-center gap-3">
                  <span className="bg-gradient-to-r from-white via-dineai-yellow to-white bg-clip-text text-transparent">
                    Ready to see what happens when restaurants implement Dine AI? The numbers speak for themselves...
                  </span>
                  <ArrowDown className="w-5 h-5 text-dineai-yellow animate-bounce" />
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;

